import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, transition, trigger, style} from '@angular/animations';
import {AbstractComponent} from "@components";
import {
  ADMINISTRATOR_ROLE,
  Category,
  Country,
  League, LeagueDetailsComponentType,
  Lookups, MultiLangField,
  Organiser, ORGANISERS_ADMINISTRATOR_ROLE, ORGANIZER_ROLE,
  TOAST_STATE
} from "@model";
import {
  CategoriesService, LeagueService, LoaderService,
  ToastService
} from "@services";
import {emailAllowEmpty, max_length_255, required} from "@utility";
import {LEAGUES_EDIT} from "../../../../../../app-routing.module";
import {Router} from "@angular/router";

@Component({
    selector: 'app-league-basic-data',
    templateUrl: './league-basic-data.component.html',
    styleUrls: ['./league-basic-data.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})
export class LeagueBasicDataComponent extends AbstractComponent implements OnInit {
  @Input() disableEditing: boolean = true;
  @Input() currentLeague: League | undefined;
  @Input() leagueDetailsComponentType: LeagueDetailsComponentType | undefined;
  @Input() organisers: Organiser[] = [];
  @Input({required:true}) sports: Lookups[] = [];
  @Input({required:true}) countries: Country[] = [];
  @Input({required:true}) languages: Lookups[] = [];
  @Input() userRole: String | undefined;
  @Output() onLeagueChange: EventEmitter<League> = new EventEmitter<League>();

  isAdministrator: boolean = false;
  isOrgAdministrator: boolean = false;
  isOrganiser: boolean = false;

  currentDate: Date = new Date();
  countriesOptions: Map<number, string> = new Map<number, string>();
  categories: Category[] = [];
  inputForm = new FormGroup({
    organiserId: new FormControl(),
    categoryId: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    start: new FormControl(),
    end: new FormControl(),
    imageMedium: new FormControl(),
    imageIcon: new FormControl(),
    defaultLanguage: new FormControl(),
    extraLanguages: new FormControl(),
    description: this.multiLangGroup(),
    rules: this.multiLangGroup(),
    generalInfo: this.multiLangGroup(),
    scoringInfo: this.multiLangGroup(),
    sportId: new FormControl(),
    countries: new FormControl(),
    maxNumberOfEvents: new FormControl(),
    roundsEnabled: new FormControl(),
    officialWebsite: new FormControl(),
    officialMail: new FormControl(),
  });


  constructor(private toastService: ToastService, public translocoService: TranslocoService,
              private leagueService: LeagueService, private router: Router,
              public loaderService: LoaderService, private categoriesService: CategoriesService) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.subscribeToDefaultLangChange();
    this.countriesOptions = this.initCountriesOptions();
    this.isAdministrator = this.userRole === ADMINISTRATOR_ROLE;
    this.isOrganiser = this.userRole === ORGANIZER_ROLE;
    this.isOrgAdministrator = this.userRole === ORGANISERS_ADMINISTRATOR_ROLE;
    if (this.isAdministrator || this.isOrgAdministrator) {
      this.subscriptions.push(
          this.inputForm.controls.organiserId.valueChanges.subscribe(value => {
            if (value) {
              this.categoriesService.findCategoriesList(value).subscribe(result => {
                this.categories = result ?? [];
              });
              if (!this.currentLeague?.id || this.currentLeague.organiserId !== value) {
                this.inputForm.controls.officialMail.setValue(this.organisers.find(o => o.id === value)?.email);
              }
            }
            else {
              this.categories = [];
              this.inputForm.controls.officialMail.setValue(undefined);
            }
            this.inputForm.controls.categoryId.setValue(undefined);
          })
      )
    }

    if (this.currentLeague?.organiserId || this.isOrganiser) {
      const orgId = this.isOrganiser ? undefined : this.currentLeague?.organiserId
      this.categoriesService.findCategoriesList(orgId).subscribe(result => {
        this.categories = result ?? []
      })
    }

    this.setCurrentLeagueData();
    if (this.currentLeague?.id) {
      this.inputForm.controls.organiserId.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableEditing'] && !changes['disableEditing'].firstChange) {
      if (this.disableEditing) {
        this.inputForm.disable();
      }
      else {
        this.inputForm.enable();
      }
    }
  }

  initCountriesOptions(): Map<number, string> {
    let countriesOptions: Map<number, string> = new Map<number, string>();
    if ((this.countries?.length ?? 0) > 0) {
      for (let i = 0; i < this.countries!.length; i++) {
        countriesOptions.set(this.countries[i].id!, this.countries[i].name ?? "");
      }
    }
    return countriesOptions;
  }

  private initForm(): void {
    this.inputForm.controls.organiserId.addValidators([required]);
    this.inputForm.controls.defaultLanguage.addValidators([required]);
    this.inputForm.controls.name.addValidators([required, max_length_255]);
    this.inputForm.controls.nameEng.addValidators([max_length_255]);
    this.inputForm.controls.nameIta.addValidators([max_length_255]);
    this.inputForm.controls.nameSlo.addValidators([max_length_255]);
    this.inputForm.controls.nameSr.addValidators([max_length_255]);
    this.inputForm.controls.nameBs.addValidators([max_length_255]);
    this.inputForm.controls.start.addValidators([required]);
    this.inputForm.controls.end.addValidators([required]);
    this.inputForm.controls.sportId.addValidators([required]);
    this.inputForm.controls.maxNumberOfEvents.addValidators([required]);
    this.inputForm.controls.countries.addValidators([required]);
    this.inputForm.controls.imageIcon.addValidators([required]);
    this.inputForm.controls.imageMedium.addValidators([required]);
    this.inputForm.controls.officialMail.addValidators([emailAllowEmpty]);
    if (this.organisers.length === 1) {
      this.inputForm.controls.organiserId.setValue(this.organisers[0].id);
    }
    if (this.disableEditing) {
      this.inputForm.disable();
    }
  }

  private subscribeToDefaultLangChange() {
    this.subscriptions.push(this.inputForm.controls.defaultLanguage.valueChanges.subscribe((langId: number) => {
      if (langId) {
        if (Array.isArray(this.inputForm.controls.extraLanguages.value)) {
          let extraLanguages: any[] = this.inputForm.controls.extraLanguages.value ? [...this.inputForm.controls.extraLanguages.value] : [];
          let index: number = extraLanguages.indexOf(langId, 0);
          if (index > -1) {
            extraLanguages.splice(index, 1);
            this.inputForm.controls.extraLanguages.setValue(extraLanguages);
          }
        }
      }
    }));
  }

  private setCurrentLeagueData(): void {
    if (this.currentLeague) {
      this.inputForm.patchValue(this.currentLeague);
      if (this.currentLeague.organiser?.id) {
        this.inputForm.controls.organiserId.setValue(this.currentLeague.organiser?.id);
      }
      this.inputForm.updateValueAndValidity();
    }
  }

  onSave(): void {
    if (this.disableEditing) {
      return;
    }
    this.markAllAsTouched(this.inputForm)
    if (!this.inputForm.valid) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("league.details.error.form"))
      return;
    } else {
      let league: League = this.getLeagueFields();
      if (this.currentLeague !== undefined) {
          this.loaderService.startLoading();
          this.leagueService.updateLeague({...league}, (updated: League) => {
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("league.details.success.basic.data.edit"))
            if (!league.id && updated.id) {
              this.router.navigate([LEAGUES_EDIT], {queryParams: {leagueId: updated.id}});
            }
            else {
              this.currentLeague = updated;
              this.onLeagueChange.emit(this.currentLeague);
            }
          }, () => {
            this.loaderService.dismissLoading();
            this.toastService.showToast(TOAST_STATE.danger,
              this.translocoService.translate("app.common.server.error"))
          });
      }
    }
  }

  getLeagueFields(): League {
    this.checkValidators(this.inputForm);
    let league: League = {...this.currentLeague}
    if (!this.currentLeague?.id) {
      league.organiserId = this.organisers?.filter((organiser: Organiser): boolean => organiser.id === this.inputForm.controls.organiserId.value)[0].id;
    }
    league.categoryId = this.inputForm.value.categoryId;
    league.name = this.inputForm.value.name;
    league.nameEng = this.inputForm.value.nameEng;
    league.nameIta = this.inputForm.value.nameIta;
    league.nameSlo = this.inputForm.value.nameSlo;
    league.nameSr = this.inputForm.value.nameSr;
    league.nameBs = this.inputForm.value.nameBs;
    league.start = this.inputForm.value.start;
    league.end = this.inputForm.value.end;
    league.defaultLanguage = this.inputForm.value.defaultLanguage;
    league.extraLanguages = this.inputForm.controls.extraLanguages.value;
    league.imageIcon = this.inputForm.value.imageIcon;
    league.imageMedium = this.inputForm.value.imageMedium;
    league.description = this.extractMultiLangValues(this.inputForm.get('description') as FormGroup);
    league.rules = this.extractMultiLangValues(this.inputForm.get('rules') as FormGroup);
    league.generalInfo = this.extractMultiLangValues(this.inputForm.get('generalInfo') as FormGroup);
    league.scoringInfo = this.extractMultiLangValues(this.inputForm.get('scoringInfo') as FormGroup);
    league.sportId = this.inputForm.value.sportId;
    league.maxNumberOfEvents = this.inputForm.value.maxNumberOfEvents;
    league.countries = this.inputForm.value.countries;
    league.roundsEnabled = this.inputForm.value.roundsEnabled;
    league.officialWebsite = this.inputForm.value.officialWebsite;
    league.officialMail = this.inputForm.value.officialMail;
    league.image = undefined;
    league.imageSmall = undefined;
    league.imageMini = undefined;
    return league;
  }

  getOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.languages.map((language: Lookups): void => {
      if (language.id && language.id !== this.inputForm.controls.defaultLanguage.value) {
        optionsMap.set(language.id, (language?.code) ? this.translocoService.translate('app.common.language.' + language.code) : "")
      }
    });
    return optionsMap;
  }

  imageChange(image: string | undefined): void {
    this.inputForm.controls.imageMedium.setValue(image);
  }

  logoChange(image: string | undefined): void {
    this.inputForm.controls.imageIcon.setValue(image);
  }

  multiLangGroup() {
    return new FormGroup({
      hr: new FormControl(),
      en: new FormControl(),
      it: new FormControl(),
      sl: new FormControl(),
      sr: new FormControl(),
      bs: new FormControl(),
    });
  }

  extractMultiLangValues(group: FormGroup): MultiLangField {
    return {
      hr: group.get('hr')?.value,
      en: group.get('en')?.value,
      it: group.get('it')?.value,
      sl: group.get('sl')?.value,
      sr: group.get('sr')?.value,
      bs: group.get('bs')?.value,
    };
  }


}


